import { useState } from "react";
import { PATHWAY } from "@/constants/testIds";
import {
  AUDIENCES,
  BUDGET_BANDS,
  INSURANCE_LINES,
  OUTPUTS,
} from "@/lib/pathwayData";
import { SectionEyebrow } from "@/components/pathway/Journey";
import { useRuntime } from "@/lib/RuntimeContext";
import {
  downloadJson,
  intakeReceipt,
  makeReference,
} from "@/lib/receipt";

const blank = {
  name: "",
  organisation: "",
  email: "",
  insuranceLine: "",
  audiences: [],
  outputs: [],
  budget: "",
  notes: "",
};

export default function IntakeForm() {
  const { status: runtime } = useRuntime();
  const [form, setForm] = useState(blank);
  const [submitted, setSubmitted] = useState(null); // { reference, receipt }

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) =>
    setForm((p) => ({
      ...p,
      [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v],
    }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.organisation || !form.email || !form.insuranceLine) {
      return;
    }
    const reference = makeReference("ERLP-INTAKE");
    const receipt = intakeReceipt(form, reference, runtime);
    setSubmitted({ reference, receipt });
    if (typeof window !== "undefined") {
      window.scrollTo({ top: window.scrollY, behavior: "instant" });
    }
  };

  const reset = () => {
    setSubmitted(null);
    setForm(blank);
  };

  return (
    <section id="intake" className="border-t hairline bg-[#0a0a0a]">
      <div className="mx-auto max-w-7xl px-6 py-24 md:px-12 md:py-32">
        <div className="grid gap-12 md:grid-cols-[1fr_1.4fr] md:gap-20">
          <div>
            <SectionEyebrow>VII. Sponsor intake</SectionEyebrow>
            <h2 className="mt-4 font-serif text-3xl leading-tight tracking-tight text-neutral-50 sm:text-4xl lg:text-5xl">
              Convene the conversation.
            </h2>
            <p className="mt-6 text-neutral-400">
              Capture the insurance line, market audience and desired
              thought-leadership output. Submission produces a synthetic
              public-safe receipt. No live data is processed. No regulated
              advice is given.
            </p>
            <ul className="mt-10 space-y-3 border-t hairline pt-6 font-mono text-[11px] uppercase tracking-[0.22em] text-neutral-500">
              <li>— sponsor-backed</li>
              <li>— university-hosted</li>
              <li>— KATLAS-governed</li>
              <li>— market-facing</li>
            </ul>
          </div>

          <div>
            {!submitted ? (
              <form
                onSubmit={onSubmit}
                data-testid={PATHWAY.intakeForm}
                className="border hairline p-8 md:p-10"
                noValidate
              >
                <Field label="Name">
                  <input
                    type="text"
                    required
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    data-testid={PATHWAY.intakeName}
                    className="editorial-input"
                    placeholder="Your name"
                  />
                </Field>
                <Field label="Organisation">
                  <input
                    type="text"
                    required
                    value={form.organisation}
                    onChange={(e) => update("organisation", e.target.value)}
                    data-testid={PATHWAY.intakeOrganisation}
                    className="editorial-input"
                    placeholder="Insurer, broker, university or sponsor"
                  />
                </Field>
                <Field label="Email">
                  <input
                    type="email"
                    required
                    value={form.email}
                    onChange={(e) => update("email", e.target.value)}
                    data-testid={PATHWAY.intakeEmail}
                    className="editorial-input"
                    placeholder="name@organisation.com"
                  />
                </Field>

                <Field label="Insurance line of interest">
                  <select
                    required
                    value={form.insuranceLine}
                    onChange={(e) => update("insuranceLine", e.target.value)}
                    data-testid={PATHWAY.intakeLine}
                    className="editorial-input"
                  >
                    <option value="" disabled style={{ background: "#0a0a0a" }}>
                      Select a line
                    </option>
                    {INSURANCE_LINES.map((l) => (
                      <option key={l} value={l} style={{ background: "#0a0a0a" }}>
                        {l}
                      </option>
                    ))}
                  </select>
                </Field>

                <FieldGroup label="Target market audience">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {AUDIENCES.map((a) => (
                      <CheckPill
                        key={a.slug}
                        checked={form.audiences.includes(a.label)}
                        onChange={() => toggle("audiences", a.label)}
                        testId={PATHWAY.intakeAudience(a.slug)}
                        label={a.label}
                      />
                    ))}
                  </div>
                </FieldGroup>

                <FieldGroup label="Desired thought-leadership output">
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {OUTPUTS.map((o) => (
                      <CheckPill
                        key={o.slug}
                        checked={form.outputs.includes(o.label)}
                        onChange={() => toggle("outputs", o.label)}
                        testId={PATHWAY.intakeOutput(o.slug)}
                        label={o.label}
                      />
                    ))}
                  </div>
                </FieldGroup>

                <Field label="Indicative budget band">
                  <select
                    value={form.budget}
                    onChange={(e) => update("budget", e.target.value)}
                    data-testid={PATHWAY.intakeBudget}
                    className="editorial-input"
                  >
                    <option value="" style={{ background: "#0a0a0a" }}>
                      Optional — to be discussed
                    </option>
                    {BUDGET_BANDS.map((b) => (
                      <option key={b} value={b} style={{ background: "#0a0a0a" }}>
                        {b}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Notes">
                  <textarea
                    rows={4}
                    value={form.notes}
                    onChange={(e) => update("notes", e.target.value)}
                    data-testid={PATHWAY.intakeNotes}
                    className="editorial-input resize-none"
                    placeholder="Optional context, themes, partners or constraints"
                  />
                </Field>

                <div className="mt-8 flex flex-col items-start gap-4 border-t hairline pt-6 md:flex-row md:items-center md:justify-between">
                  <p className="max-w-md font-mono text-[10px] uppercase leading-relaxed tracking-[0.18em] text-neutral-500">
                    Submission produces a synthetic public-safe receipt.
                    No live processing. No regulated advice.
                  </p>
                  <button
                    type="submit"
                    data-testid={PATHWAY.intakeSubmit}
                    className="border border-amber-700 bg-amber-700 px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors hover:bg-amber-600"
                  >
                    Submit intake
                  </button>
                </div>
              </form>
            ) : (
              <ConfirmationPanel
                reference={submitted.reference}
                receipt={submitted.receipt}
                onReset={reset}
                onDownload={() =>
                  downloadJson(
                    `lab-pathway-intake-${submitted.reference}.json`,
                    submitted.receipt
                  )
                }
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="mt-6 block first:mt-0">
      <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </div>
      {children}
    </label>
  );
}

function FieldGroup({ label, children }) {
  return (
    <fieldset className="mt-8 border-t hairline pt-6">
      <legend className="font-mono text-[10px] uppercase tracking-[0.24em] text-neutral-500">
        {label}
      </legend>
      <div className="mt-4">{children}</div>
    </fieldset>
  );
}

function CheckPill({ checked, onChange, testId, label }) {
  return (
    <label className="flex cursor-pointer items-center gap-3 border hairline px-3 py-2.5 text-[13px] text-neutral-200 transition-colors hover:border-neutral-500">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        data-testid={testId}
        className="h-3.5 w-3.5 accent-amber-700"
      />
      <span>{label}</span>
    </label>
  );
}

function ConfirmationPanel({ reference, receipt, onReset, onDownload }) {
  return (
    <div
      data-testid={PATHWAY.intakeConfirmation}
      className="border hairline bg-[#0f0f0f] p-8 md:p-10 receipt-grain"
    >
      <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-600">
        Intake acknowledged · synthetic record
      </div>
      <h3 className="mt-4 font-serif text-2xl leading-snug text-neutral-50 sm:text-3xl">
        Your sponsor intake has been captured.
      </h3>
      <p className="mt-5 text-[15px] leading-relaxed text-neutral-300">
        Thank you. A KATLAS / Cierge Labs sponsor desk receives this synthetic
        intake record and will respond with a sponsor pathway proposal and an
        indicative budget band.
      </p>

      <div className="mt-8 border hairline p-5 font-mono text-xs text-neutral-300">
        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500">
          reference
        </div>
        <div
          data-testid={PATHWAY.intakeReference}
          className="mt-1 break-all text-amber-500"
        >
          {reference}
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.24em] text-neutral-500">
          verification_state
        </div>
        <div className="mt-1 text-[#dc2626]">
          {receipt.verification_state}
        </div>
      </div>

      <div className="mt-8 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
        <button
          type="button"
          onClick={onDownload}
          data-testid={PATHWAY.intakeReceiptDownload}
          className="border border-amber-700 bg-amber-700 px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] text-white transition-colors hover:bg-amber-600"
        >
          Download intake receipt (JSON)
        </button>
        <button
          type="button"
          onClick={onReset}
          data-testid={PATHWAY.intakeReset}
          className="border hairline px-5 py-3 font-mono text-xs uppercase tracking-[0.22em] text-neutral-200 transition-colors hover:border-neutral-500"
        >
          Submit another intake
        </button>
      </div>
    </div>
  );
}
